import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import useOnScreen from '../../services/hooks/useOnScreen';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import { SPOT_MIN_REQ_PHOTOS } from '../../consts/spot.consts';
import RequestMorePhotos from '../../components/request-more-photos';
import ModalMobile from '../../components/modal-mobile';
import PictureSet from '../../components/picture-set';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgChevronLeft from 'src/assets/svg/SvgChevronLeft.svg';
import SvgCross2 from 'src/assets/svg/SvgCross2.svg';
import './desktop-photo-gallery.scss';

const perPage = 10;

const DesktopPhotoGallery = ({
    photos = [],
    currentPhoto,
    isOpen,
    toggle,
    hostId,
    spotId,
    handleNext,
    handleBack,
    title,
    state,
    city,
    setDesktopPhotoCount,
    guestReq = true,
    publicPark,
}) => {
    const [loading, setLoading] = useState(false);
    const photo = photos[currentPhoto];
    const altImg = title ? `${title} - ${city}, ${state}` : 'Spot thumbnail';
    const photosBelowMin = !publicPark && guestReq && photos.length < SPOT_MIN_REQ_PHOTOS;
    const length = !publicPark && photosBelowMin ? photos.length + 1 : photos.length;
    const showReq = !publicPark && photosBelowMin && currentPhoto == photos.length;
    const nextPhoto = currentPhoto < photos.length - 1 ? photos[currentPhoto + 1] : null;
    const nextPhoto2 = currentPhoto < photos.length - 2 ? photos[currentPhoto + 2] : null;
    let submittedBy = '';

    if (!showReq && photo?.providedById !== null && photo?.providedBy && hostId !== photo?.providedBy.id) {
        const monthYear = moment(photo?.createdAt).format('MMM YYYY');
        submittedBy = `Submitted by: ${photo?.providedBy.firstname} ${photo?.providedBy.lastInitial}. - ${monthYear}`;
    }

    const myRef = useRef(null);
    const [thumbRefNode, setThumbRefNode] = useState({});
    const myThumbRef = useCallback((node) => isOpen && setThumbRefNode({ current: node }), [isOpen]);
    const isVisible = useOnScreen(myRef);
    const isThumbVisible = useOnScreen(thumbRefNode);
    const [limit, setLimit] = useState(perPage > photos.length ? photos.length : perPage);
    const [limitThumb, setLimitThumb] = useState(perPage > photos.length ? photos.length : perPage);

    const limitPhotos = useMemo(() => photos.slice(0, limit), [photos, limit]);
    const limitThumbPhotos = useMemo(() => photos.slice(0, limitThumb), [photos, limitThumb]);

    useEffect(() => {
        if (!isVisible || limit == photos.length) {
            return;
        }
        const newLimit = limit + perPage > photos.length ? photos.length : limit + perPage;
        setLimit(newLimit);
    }, [isVisible]);

    useEffect(() => {
        if (!isThumbVisible || limitThumb == photos.length) {
            return;
        }
        const newLimit = limitThumb + perPage > photos.length ? photos.length : limitThumb + perPage;
        setLimitThumb(newLimit);
    }, [isThumbVisible]);

    useEffect(() => {
        if (currentPhoto > -1) {
            setLoading(true);
            setTimeout(() => setLoading(false), 20);
            const myThumb = document.querySelector('.desktop-photo-carousel-modal__content__thumbnail .active');
            myThumb?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [currentPhoto]);

    if (!showReq && !photo) {
        return '';
    }

    return (
        <ModalMobile isOpen={isOpen} toggle={toggle} className="desktop-photo-carousel-modal">
            <div className="desktop-photo-carousel-modal__content">
                <div className="photo-modal-close" onClick={toggle}>
                    <SvgCross2 />
                </div>
                <div className="desktop-photo-carousel-modal__content__thumbnail">
                    {limitThumbPhotos.map((p, i) => (
                        <div key={i} className={currentPhoto == i ? 'active' : ''} onClick={() => setDesktopPhotoCount(i)}>
                            <PictureSet
                                imgs={ImageHelper.getUrlFromOptions(p.imageAws, `full-fit-in/${IMG_RESIZES.x256}`)}
                                width={154}
                                height={154}
                                alt={altImg}
                            />
                        </div>
                    ))}
                    {photosBelowMin && (
                        <div
                            className={`desktop-photo-carousel-modal__content__thumbnail_req ${showReq ? 'active' : ''}`}
                            onClick={() => setDesktopPhotoCount(photos.length)}
                        >
                            Photo Request
                        </div>
                    )}
                    {limitThumb < photos.length && <div ref={myThumbRef} className="pb-05" />}
                </div>
                <div className="desktop-photo-carousel-modal__content__carousel">
                    {currentPhoto > 0 && (
                        <div className="photo-modal-back cursor-pointer" onClick={handleBack}>
                            <SvgChevronLeft />
                        </div>
                    )}
                    {limitPhotos.map((p, i) => {
                        const pMonthYear = moment(p.createdAt).format('MMM YYYY');
                        const pSubmittedBy = `Submitted by: ${p.providedBy?.firstname} ${p.providedBy?.lastInitial}. - ${pMonthYear}`;
                        const isLastIdx = limitPhotos.length - 1 == i;

                        return (
                            <div key={i} ref={isLastIdx ? myRef : undefined} className={`photo-modal-item md ${i == 0 ? 'mt-0' : ''}`}>
                                {!loading ? (
                                    <>
                                        <PictureSet
                                            imgs={ImageHelper.getUrlFromOptions(p.imageAws, `full-fit-in/${IMG_RESIZES.x1024}`)}
                                            width="100%"
                                            height={700}
                                            className="photos-item"
                                            alt={altImg}
                                        />
                                        {p.providedBy?.id && (
                                            <div className="photos-submitted-box">
                                                <p className="spot-detail-photos-submitted mb-0">
                                                    <span className="photos-text">{pSubmittedBy}</span>
                                                </p>
                                            </div>
                                        )}
                                        {p.caption && <p className="photo-modal-item-md-caption">{p.caption}</p>}
                                    </>
                                ) : (
                                    ''
                                )}
                            </div>
                        );
                    })}
                    {photosBelowMin && (
                        <div className="photo-modal-item md">
                            <RequestMorePhotos id={spotId} />
                        </div>
                    )}
                    <div className="photo-modal-item xl">
                        {showReq ? (
                            <RequestMorePhotos id={spotId} />
                        ) : !loading ? (
                            <>
                                <div className="w-100 h-100">
                                    <PictureSet
                                        imgs={ImageHelper.getUrlFromOptions(photo.imageAws, `fit-in/${IMG_RESIZES.x1536}`)}
                                        className="photos-item"
                                        alt={altImg}
                                        method="eager"
                                    />
                                    {photo.caption && <p className="snif-p mt-05">{photo.caption}</p>}
                                </div>
                                <div className="d-none">
                                    {nextPhoto && (
                                        <PictureSet
                                            imgs={ImageHelper.getUrlFromOptions(nextPhoto.imageAws, `fit-in/${IMG_RESIZES.x1536}`)}
                                            method="eager"
                                        />
                                    )}
                                    {nextPhoto2 && (
                                        <PictureSet
                                            imgs={ImageHelper.getUrlFromOptions(nextPhoto2.imageAws, `fit-in/${IMG_RESIZES.x1536}`)}
                                            method="eager"
                                        />
                                    )}
                                </div>
                                {submittedBy && (
                                    <div className="photos-submitted-box">
                                        <p className="spot-detail-photos-submitted mb-0">
                                            <span className="photos-text">{submittedBy}</span>
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            ''
                        )}
                        <p className="spot-detail-photos-counter snif-s2 snif-medium mb-0">
                            <span className="photos-text">{`${currentPhoto + 1} of ${length}`}</span>
                        </p>
                    </div>
                    {currentPhoto < length - 1 && (
                        <div className="photo-modal-next cursor-pointer" onClick={handleNext}>
                            <SvgChevronRight />
                        </div>
                    )}
                </div>
            </div>
        </ModalMobile>
    );
};

export default DesktopPhotoGallery;
